/**
 * Analytical model of the globe's camera transform.
 *
 * WHY THIS EXISTS. Two things need to know how many screen pixels a degree of
 * arc is worth, and neither can ask MapLibre:
 *
 *  - `magnifyForContenders()` has to compute, at tap time, the zoom at which a
 *    cluster of near-coincident dots becomes individually tappable. It could
 *    read `map.transform` for that, but the arithmetic is the same and a pure
 *    function is testable.
 *  - The selectability tests. A headless agent cannot instantiate MapLibre —
 *    there is no WebGL context and `requestAnimationFrame` never fires, so the
 *    map never emits `load` — so "every country in the pool is reachable at
 *    this tier's default view" can only be PROVEN against the projection maths
 *    directly. `snap.test.ts` drives `snapPick` through this model.
 *
 * ACCURACY. MapLibre's globe below the mercator transition zoom is the
 * mercator plane wrapped onto a sphere whose circumference is the mercator
 * world size, viewed down the centre axis. That makes it orthographic with
 *
 *     radius_px = 512 * 2^zoom / (2*pi)
 *
 * which is what this module implements. MapLibre's camera is very slightly
 * perspective, so limb positions here are approximate to within a couple of
 * percent; near the view centre the two agree. Every consumer either works in
 * relative separations or has a comfortable margin, so that is fine — but this
 * is a MODEL, not a measurement, and nothing here has been checked against a
 * real GPU frame.
 */

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/** MapLibre's tile size. `worldSize = TILE_SIZE * 2^zoom`. */
export const GLOBE_TILE_SIZE = 512;

/** Radius of the rendered sphere in CSS pixels. */
export function globeRadiusPx(zoom: number): number {
  return (GLOBE_TILE_SIZE * Math.pow(2, zoom)) / (2 * Math.PI);
}

/**
 * Pixels per degree of arc *at the centre of the visible disc*. Away from the
 * centre the radial component foreshortens by cos(angle from centre); the
 * tangential component does not.
 */
export function globePxPerDeg(zoom: number): number {
  return (GLOBE_TILE_SIZE * Math.pow(2, zoom)) / 360;
}

/** Inverse of {@link globePxPerDeg}. */
export function zoomForPxPerDeg(pxPerDeg: number): number {
  return Math.log2((Math.max(1e-9, pxPerDeg) * 360) / GLOBE_TILE_SIZE);
}

/** The zoom at which the rendered globe disc has an exact CSS-pixel diameter. */
export function zoomForGlobeDiameter(diameterPx: number): number {
  return Math.log2((Math.max(1, diameterPx) * Math.PI) / GLOBE_TILE_SIZE);
}

/**
 * The zoom at which the globe spans `fraction` of a viewport's short edge.
 *
 * The sphere's radius is `worldSize / (2*pi)`, so its rendered diameter is
 * `worldSize / pi`. Solve that for the diameter we want, then clamp to the
 * camera's own limits so a freak viewport cannot produce an illegal zoom.
 */
export function homeZoomFor(
  shortEdgePx: number,
  fraction: number,
  minZoom: number,
  maxZoom: number,
): number {
  const diameterPx = Math.max(1, shortEdgePx * fraction);
  const z = zoomForGlobeDiameter(diameterPx);
  return Math.min(maxZoom, Math.max(minZoom, z));
}

export interface ViewState {
  /** `[longitude, latitude]` the camera is centred on. */
  readonly center: readonly [number, number];
  readonly zoom: number;
  /** Canvas size in CSS pixels. */
  readonly width: number;
  readonly height: number;
}

/**
 * Screen position of a lng/lat, or null when it is on the far side of the
 * planet. Origin is the canvas top-left; y grows downward.
 */
export function projectGlobe(
  view: ViewState,
  lngLat: readonly [number, number],
): [number, number] | null {
  const r = globeRadiusPx(view.zoom);
  const lat0 = view.center[1] * DEG;
  const lat = lngLat[1] * DEG;
  const dLng = (lngLat[0] - view.center[0]) * DEG;

  const cosC = Math.sin(lat0) * Math.sin(lat) + Math.cos(lat0) * Math.cos(lat) * Math.cos(dLng);
  if (cosC <= 0) return null;

  const x = r * Math.cos(lat) * Math.sin(dLng);
  const y = r * (Math.cos(lat0) * Math.sin(lat) - Math.sin(lat0) * Math.cos(lat) * Math.cos(dLng));
  return [view.width / 2 + x, view.height / 2 - y];
}

/** Inverse of {@link projectGlobe}. Null for a pixel off the sphere. */
export function unprojectGlobe(
  view: ViewState,
  px: readonly [number, number],
): [number, number] | null {
  const r = globeRadiusPx(view.zoom);
  const x = px[0] - view.width / 2;
  const y = view.height / 2 - px[1];
  const rho = Math.hypot(x, y);
  if (rho > r) return null;
  if (rho < 1e-9) return [view.center[0], view.center[1]];

  const c = Math.asin(Math.min(1, rho / r));
  const lat0 = view.center[1] * DEG;
  const sinC = Math.sin(c);
  const cosC = Math.cos(c);

  const lat = Math.asin(cosC * Math.sin(lat0) + (y * sinC * Math.cos(lat0)) / rho);
  const lng =
    view.center[0] * DEG +
    Math.atan2(x * sinC, rho * cosC * Math.cos(lat0) - y * sinC * Math.sin(lat0));

  const wrapped = (((lng * RAD + 180) % 360) + 360) % 360 - 180;
  return [wrapped, lat * RAD];
}
